<template>
  <div class="dashboard-tab">
    <div class="dashboard-header">
      <div class="partner-title">
        <h3>{{ partner.first_name }} {{ partner.last_name }}</h3>
        <p v-if="channel">{{ channel.title }}</p>
      </div>
      <b-select v-model="selected_currency" @input="loadFundsData" class="filter-currency-input"
        placeholder="Select Currency">
        <option value="RWF">RWF</option>
        <option value="USD">USD</option>
      </b-select>
    </div>

    <div v-if="loading" class="loading-panel">
      <div class="overview-dash">
        <div class="widget-counter skeleton" v-for="index in 6" :key="index">
          <b-skeleton height="80px"></b-skeleton>
        </div>
      </div>
    </div>

    <div v-else class="dashboard-content">
      <div class="section-title">Activity</div>
      <div class="overview-dash">
        <widgetCounter title="Active Streams" :value="active_streams">
          <img src="@/assets/images/dashboard/Graph-1.svg" />
        </widgetCounter>
        <widgetCounter title="Total Streams" :value="total_streams">
          <img src="@/assets/images/dashboard/Graph-2.svg" />
        </widgetCounter>
        <widgetCounter title="Active Ticket Events" :value="active_ticket_events">
          <img src="@/assets/images/dashboard/Graph-3.svg" />
        </widgetCounter>
        <widgetCounter title="Total Ticket Events" :value="total_ticket_events">
          <img src="@/assets/images/dashboard/Graph-4.svg" />
        </widgetCounter>
        <widgetCounter title="Active Voting Sessions" :value="active_voting">
          <img src="@/assets/images/dashboard/Graph-5.svg" />
        </widgetCounter>
        <widgetCounter title="Total Voting Sessions" :value="total_voting">
          <img src="@/assets/images/dashboard/Graph-6.svg" />
        </widgetCounter>
        <widgetCounter title="Total Subscribers" :value="total_subscribers">
          <img src="@/assets/images/dashboard/Graph-1.svg" />
        </widgetCounter>
        <widgetCounter title="Active Subscriptions" :value="active_subscriptions">
          <img src="@/assets/images/dashboard/Graph-2.svg" />
        </widgetCounter>
      </div>

      <div class="section-title">Earnings ({{ selected_currency }})</div>
      <div class="overview-dash">
        <widgetCounter title="Available Balance" :value="formatMoney(funds.available_balance)">
          <img src="@/assets/images/dashboard/Graph-1.svg" />
        </widgetCounter>
        <widgetCounter title="Total Earned" :value="formatMoney(total_earned)">
          <img src="@/assets/images/dashboard/Graph-2.svg" />
        </widgetCounter>
        <widgetCounter title="Streaming Balance" :value="formatMoney(funds.streaming_balance)">
          <img src="@/assets/images/dashboard/Graph-3.svg" />
        </widgetCounter>
        <widgetCounter title="Voting Balance" :value="formatMoney(funds.voting_balance)">
          <img src="@/assets/images/dashboard/Graph-4.svg" />
        </widgetCounter>
        <widgetCounter title="Ticket Events Balance" :value="formatMoney(funds.events_balance)">
          <img src="@/assets/images/dashboard/Graph-5.svg" />
        </widgetCounter>
        <widgetCounter title="Subscription Balance" :value="formatMoney(funds.subscription_balance)">
          <img src="@/assets/images/dashboard/Graph-6.svg" />
        </widgetCounter>
        <widgetCounter title="Settled Balance" :value="formatMoney(funds.settlement_balance)">
          <img src="@/assets/images/dashboard/Graph-1.svg" />
        </widgetCounter>
        <widgetCounter title="Tax Amount" :value="formatMoney(funds.tax_balance)">
          <img src="@/assets/images/dashboard/Graph-2.svg" />
        </widgetCounter>
        <widgetCounter title="Donation Balance" :value="formatMoney(funds.donation_balance)">
          <img src="@/assets/images/dashboard/Graph-3.svg" />
        </widgetCounter>
      </div>

      <widgetNotfound v-if="!channel" title="No channel linked"
        subtitle="Streaming and voting counts require a linked channel. Ticket events are still counted separately.">
      </widgetNotfound>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    partner: {
      type: Object,
      required: true,
    },
    channel: {
      type: Object,
      default: null,
    },
  },
  data: () => ({
    loading: false,
    selected_currency: "RWF",
    active_streams: 0,
    total_streams: 0,
    active_ticket_events: 0,
    total_ticket_events: 0,
    active_voting: 0,
    total_voting: 0,
    total_subscribers: 0,
    active_subscriptions: 0,
    funds: {
      available_balance: 0,
      streaming_balance: 0,
      voting_balance: 0,
      events_balance: 0,
      subscription_balance: 0,
      settlement_balance: 0,
      tax_balance: 0,
      donation_balance: 0,
    },
  }),
  computed: {
    total_earned() {
      const funds = this.funds;
      return (
        (funds.streaming_balance || 0) +
        (funds.voting_balance || 0) +
        (funds.events_balance || 0) +
        (funds.subscription_balance || 0) +
        (funds.donation_balance || 0)
      );
    },
  },
  watch: {
    partner: {
      immediate: true,
      handler() {
        this.loadDashboardData();
      },
    },
    channel() {
      this.loadActivityData();
    },
  },
  methods: {
    formatMoney(amount) {
      return this.$formatMoney(
        amount || 0,
        2,
        ".",
        ",",
        this.selected_currency
      );
    },
    loadDashboardData() {
      if (!this.partner) return;
      this.loading = true;
      Promise.all([this.loadActivityData(), this.loadFundsData()])
        .finally(() => {
          this.loading = false;
        });
    },
    loadActivityData() {
      if (!this.partner) return Promise.resolve();

      const requests = [
        this.$store.dispatch("ADMIN_USER_GET_SUBSCRIPTION_REPORT", {
          user_id: this.partner.id,
        }).then((response) => {
          const data = response.data.return || {};
          this.total_subscribers = data.total_subscribers || 0;
          this.active_subscriptions = data.active_subscriptions || 0;
        }).catch(() => {
          this.total_subscribers = 0;
          this.active_subscriptions = 0;
        }),
        this.$store.dispatch("EVENTS_GET_ALL_EVENT", {
          user_id: this.partner.id,
        }).then((response) => {
          const ticketEvents = response.data || [];
          this.total_ticket_events = ticketEvents.length;
          this.active_ticket_events = ticketEvents.filter(
            (item) => item.status === "public" || item.status === "private"
          ).length;
        }).catch(() => {
          this.total_ticket_events = 0;
          this.active_ticket_events = 0;
        }),
      ];

      if (this.channel) {
        requests.push(
          this.$store.dispatch("CHANNEL_GET_ALL_EVENTS", {
            channel_id: this.channel.id,
          }).then((response) => {
            const streams = response.data.return || [];
            this.total_streams = streams.length;
            this.active_streams = streams.filter((item) => item.status === "active").length;
          }).catch(() => {
            this.total_streams = 0;
            this.active_streams = 0;
          }),
          this.$store.dispatch("CHANNEL_GET_ALL_VOTING_SESSIONS", {
            channel_id: this.channel.id,
            user_id: this.partner.id,
          }).then((response) => {
            const sessions = response.data.return || [];
            this.total_voting = sessions.length;
            this.active_voting = sessions.filter((item) => item.status === "public").length;
          }).catch(() => {
            this.total_voting = 0;
            this.active_voting = 0;
          })
        );
      } else {
        this.total_streams = 0;
        this.active_streams = 0;
        this.total_voting = 0;
        this.active_voting = 0;
      }

      return Promise.all(requests);
    },
    loadFundsData() {
      if (!this.partner) return Promise.resolve();

      return this.$store
        .dispatch("ADMIN_USER_FUNDS_OVERVIEW", {
          user_id: this.partner.id,
          currency: this.selected_currency,
        })
        .then((response) => {
          const data = response.data.return || {};
          this.funds = {
            available_balance: data.available_balance || 0,
            streaming_balance: data.streaming_balance || 0,
            voting_balance: data.voting_balance || 0,
            events_balance: data.events_balance || 0,
            subscription_balance: data.subscription_balance || 0,
            settlement_balance: data.settlement_balance || 0,
            tax_balance: data.tax_balance || 0,
            donation_balance: data.donation_balance || 0,
          };
        })
        .catch(() => {
          this.funds = {
            available_balance: 0,
            streaming_balance: 0,
            voting_balance: 0,
            events_balance: 0,
            subscription_balance: 0,
            settlement_balance: 0,
            tax_balance: 0,
            donation_balance: 0,
          };
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.dashboard-tab {
  padding: 15px;
  height: 100%;
  overflow-y: auto;

  .dashboard-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    gap: 15px;
    flex-wrap: wrap;

    .partner-title {
      h3 {
        font-size: 22px;
        font-weight: 700;
        margin: 0;
      }

      p {
        margin: 4px 0 0;
        color: #777;
      }
    }

    .filter-currency-input {
      min-width: 120px;
    }
  }

  .section-title {
    font-size: 16px;
    font-weight: 600;
    color: #5e6c84;
    margin: 10px 0 12px;
  }

  .overview-dash {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 10px;

    .widget-counter {
      width: calc(33.333% - 10px);
      margin-bottom: 10px;

      @media (max-width: 1100px) {
        width: calc(50% - 10px);
      }

      @media (max-width: 650px) {
        width: 100%;
      }
    }

    .skeleton {
      border: none;
      padding: 0;
    }
  }
}
</style>
